import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ValidationContainer } from '@skbkontur/react-ui-validations';
import { normalizeObject } from '../../helpers/normalizeObject';
import { parseDocumentNumber } from '../../helpers/parseDocumentNumber';
import { checkDigitsOnly, checkNotEmpty, validateComboBox } from '../../helpers/validators';
import { checkDateInRange } from '../../helpers/validators';
import { ActNoteField } from '../ActNoteField';
import { BirthdayField, validateBirthDay } from '../BirthdayField';
import { checkName, FullNameField } from '../FullNameField';
import { Gender } from '../Gender';
import { DocumentIssuerField } from '../IdentityDocumentForm/components/DocumentIssuerField/DocumentIssuerField';
import { checkIssuerMaxLegth } from '../IdentityDocumentForm/components/DocumentIssuerField/DocumentIssuerField.validation';
import { DocumentIssuerUnitCodeField, validateDocumentIssuerUnitCode, } from '../IdentityDocumentForm/components/DocumentIssuerUnitCodeField';
import { DocumentNumberField, validateDocumentNumber } from '../IdentityDocumentForm/components/DocumentNumberField';
import { DocumentPeriodField } from '../IdentityDocumentForm/components/DocumentPeriodFields/DocumentPeriodField';
import { DocumentTypeField } from '../IdentityDocumentForm/components/DocumentTypeField';
import { RelationshipField } from '../RelationshipField/RelationshipField';
import { SidePageFormTitle } from '../SidePageFormTitle';
import { SnilsField, validateSnils } from '../SnilsField';
import classes from './RelativeInfoForm.module.css';
const DEFAULT_RELATIVE_DATA = {
    name: {
        lastName: '',
    },
    sex: undefined,
    birthday: undefined,
    relationship: undefined,
    snils: undefined,
    document: undefined,
    actNote: undefined,
    id: '',
    employeeId: '',
    employerId: '',
    meta: {
        version: 0,
    },
};
const INPUTS_WIDTH = 200;
export const RelativeInfoForm = ({ formRef, initialState, relationshipList, onSubmit, documentReferences, disabled, currentRelativeInfoRef, trackValidationError, }) => {
    const [relativeInfo, setRelativeInfo] = useState(initialState || DEFAULT_RELATIVE_DATA);
    const refValidationContainer = useRef(null);
    const relativeDocumentFullNumber = relativeInfo.document
        ? `${relativeInfo.document.series || ''}${relativeInfo.document.series ? ' ' : ''}${relativeInfo.document.number || ''}`
        : '';
    const changeRelativeInfoValue = (v, stateKey) => {
        setRelativeInfo({
            ...relativeInfo,
            [stateKey]: v,
        });
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        const isValid = (await refValidationContainer.current?.validate()) || false;
        if (!onSubmit || !isValid) {
            trackValidationError?.(relativeInfo);
            return;
        }
        onSubmit(normalizeObject(relativeInfo));
    };
    useEffect(() => {
        if (currentRelativeInfoRef) {
            currentRelativeInfoRef.current = relativeInfo;
        }
    }, [relativeInfo]);
    const documentTypeValue = useMemo(() => {
        return {
            code: relativeInfo.document?.type || '',
            name: documentReferences.find((el) => el.code === relativeInfo.document?.type)?.name || '',
        };
    }, [documentReferences, relativeInfo.document?.type]);
    const relativeIdentityDocumentValidityPeriods = {
        begin: relativeInfo.document?.validityPeriod?.begin || '',
        end: relativeInfo.document?.validityPeriod?.end || '',
    };
    const isDocumentSomeDataFilled = [
        relativeInfo.document?.type,
        relativeInfo.document?.number,
        relativeInfo.document?.series,
        relativeIdentityDocumentValidityPeriods.begin,
        relativeIdentityDocumentValidityPeriods.end,
        relativeInfo.document?.issuer,
        relativeInfo.document?.issuerUnitCode,
    ].some((v) => Boolean(v));
    return (_jsx("form", { onSubmit: handleSubmit, ref: formRef, children: _jsx(ValidationContainer, { ref: refValidationContainer, children: _jsxs("div", { className: classes.relativeFormContainer, children: [_jsx(FullNameField, { value: relativeInfo.name, onValueChange: (field, v) => {
                            changeRelativeInfoValue({
                                ...relativeInfo.name,
                                [field]: v,
                            }, 'name');
                        }, vertical: true, labelClassName: clsx(classes.label), validationInfo: {
                            firstName: checkName({ value: relativeInfo.name.firstName || '', required: true }),
                            lastName: checkName({ value: relativeInfo.name.lastName, required: true }),
                            middleName: checkName({ value: relativeInfo.name.middleName || '', required: false }),
                        }, width: INPUTS_WIDTH, disabled: disabled }), _jsx(BirthdayField, { value: relativeInfo.birthday || '', onValueChange: (v) => changeRelativeInfoValue(v, 'birthday'), labelClassName: clsx(classes.label), validationInfo: validateBirthDay({ value: relativeInfo.birthday || '', required: true }), disabled: disabled }), _jsx(Gender, { value: relativeInfo.sex, onValueChange: (v) => changeRelativeInfoValue(v, 'sex'), labelClassName: clsx(classes.label), disabled: disabled }), _jsx(SnilsField, { value: relativeInfo.snils || '', onValueChange: (v) => changeRelativeInfoValue(v, 'snils'), labelClassName: clsx(classes.label), validationInfo: validateSnils({ value: relativeInfo.snils || '' }), disabled: disabled }), _jsx(RelationshipField, { value: relativeInfo.relationship || '', onValueChange: (v) => changeRelativeInfoValue(v, 'relationship'), relationshipList: relationshipList, labelClassName: clsx(classes.label), validationInfo: checkNotEmpty({
                            value: relativeInfo.relationship,
                            message: 'Выберите значение из справочника',
                        }), disabled: disabled }), _jsx(SidePageFormTitle, { text: 'Документ, удостоверяющий личность родственника' }), _jsx(DocumentTypeField, { documentsList: documentReferences, onValueChange: (v) => changeRelativeInfoValue({ ...relativeInfo.document, type: v.code }, 'document'), value: documentTypeValue, labelClassName: clsx(classes.label), validationInfo: validateComboBox({
                            value: documentTypeValue,
                            items: documentReferences,
                            required: isDocumentSomeDataFilled,
                        }), disabled: disabled }), _jsx(DocumentNumberField, { onValueChange: (v) => {
                            const documentNumbers = parseDocumentNumber({
                                documentNumber: v,
                                documentType: documentTypeValue.code,
                            });
                            changeRelativeInfoValue({
                                ...relativeInfo.document,
                                number: documentNumbers.number,
                                series: documentNumbers.series,
                            }, 'document');
                        }, value: relativeDocumentFullNumber, documentType: relativeInfo.document?.type || '', labelClassName: classes.label, validationInfo: validateDocumentNumber({
                            value: relativeDocumentFullNumber,
                            required: isDocumentSomeDataFilled,
                            documentType: relativeInfo.document?.type || '',
                        }), disabled: disabled }), _jsx(DocumentPeriodField, { labelClassName: classes.label, validationInfo: checkDateInRange({
                            value: relativeIdentityDocumentValidityPeriods.begin,
                            type: 'lostfocus',
                            maxDate: relativeIdentityDocumentValidityPeriods.end,
                        }), value: relativeIdentityDocumentValidityPeriods.begin, onValueChange: (v) => changeRelativeInfoValue({
                            ...relativeInfo.document,
                            validityPeriod: {
                                ...relativeInfo.document?.validityPeriod,
                                begin: v,
                            },
                        }, 'document'), type: "begin", disabled: disabled }), _jsx(DocumentPeriodField, { labelClassName: classes.label, validationInfo: checkDateInRange({
                            value: relativeIdentityDocumentValidityPeriods.end,
                            type: 'lostfocus',
                            minDate: relativeIdentityDocumentValidityPeriods.begin,
                        }), value: relativeIdentityDocumentValidityPeriods.end, onValueChange: (v) => changeRelativeInfoValue({
                            ...relativeInfo.document,
                            validityPeriod: {
                                ...relativeInfo.document?.validityPeriod,
                                end: v,
                            },
                        }, 'document'), type: "end", disabled: disabled }), _jsx(DocumentIssuerField, { labelClassName: clsx(classes.label, classes.textareaLabel), value: relativeInfo.document?.issuer || '', onValueChange: (v) => changeRelativeInfoValue({ ...relativeInfo.document, issuer: v }, 'document'), validationInfo: checkIssuerMaxLegth({ value: relativeInfo.document?.issuer || '' }), disabled: disabled }), _jsx(DocumentIssuerUnitCodeField, { "data-tid": "issuerUnitCodeInput", onValueChange: (v) => changeRelativeInfoValue({ ...relativeInfo.document, issuerUnitCode: v }, 'document'), value: relativeInfo.document?.issuerUnitCode || '', labelClassName: classes.label, documentType: documentTypeValue.code, validationInfo: validateDocumentIssuerUnitCode({
                            value: relativeInfo.document?.issuerUnitCode || '',
                            documentType: documentTypeValue.code,
                        }), disabled: disabled }), _jsx(ActNoteField, { labelClassName: classes.label, onValueChange: (v) => changeRelativeInfoValue({ ...v }, 'actNote'), value: relativeInfo.actNote, validationInfo: {
                            inputValidation: relativeInfo.actNote?.number
                                ? checkDigitsOnly({
                                    value: relativeInfo.actNote?.number || '',
                                    type: 'lostfocus',
                                })
                                : null,
                            datePickerValidation: checkDateInRange({ value: relativeInfo.actNote?.date || '' }),
                        }, disabled: disabled })] }) }) }));
};
